import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Database, X, Square } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminHydrateNonDividendUniverseButton — Round 95z (Jun 2026).
 *
 * Section 1a of the Non-Dividend Stock Expansion. One-time bulk
 * hydration of non-dividend tickers from `us_market_universe` into
 * `stock_cache` so the upcoming 3-way segmented control (Dividend /
 * All / Non-Dividend) has data to render.
 *
 * Pipeline (server-side):
 *   1. Massive Full Market Snapshot (1 call, all 10k+ prices)
 *   2. Resolve targets where `is_non_dividend=true` AND not yet cached
 *   3. Massive Ticker Overview per target (20-concurrent)
 *   4. Compose minimal `stock_cache` rows with pays_dividend=false
 *   5. Bulk-insert
 *
 * The button is idempotent — once the universe is hydrated, subsequent
 * runs find 0 targets and complete instantly. This admin panel auto-
 * polls progress every 3 sec while a run is active.
 */
export default function AdminHydrateNonDividendUniverseButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  // Always-mounted: fetch initial status so the badge can show
  // pending-targets count even before opening the modal.
  useEffect(() => {
    if (user?.role !== 'admin') return;
    refreshStatus();
  }, [user]);

  const refreshStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/hydrate-non-dividend-universe/status`,
        { withCredentials: true }
      );
      setStatus(data);
      const isRunning = data?.in_progress_total != null
        && data?.in_progress_processed != null
        && data.in_progress_processed < data.in_progress_total;
      setRunning(!!isRunning);
    } catch (err) {
      // Silent fail — admin probably not logged in or endpoint not
      // ready yet.
    }
  };

  useEffect(() => {
    if (!running) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(refreshStatus, 3000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [running]);

  if (user?.role !== 'admin') return null;

  const startHydration = async () => {
    setStarting(true);
    setRunning(true);
    try {
      // Long-running request — kick it off and don't await the
      // response in UI (we poll status instead). Browser fetch has a
      // timeout cap; if the request actually returns we re-render.
      toast.info('Hydration started — this takes ~10-15 min for 4,500 tickers');
      const { data } = await axios.post(
        `${API}/admin/hydrate-non-dividend-universe`,
        null,
        { withCredentials: true, timeout: 30 * 60 * 1000 }
      );
      toast.success(`Hydration complete — inserted ${data.inserted} of ${data.targets} targets`, {
        description: `Errors: ${data.errors} · Elapsed: ${data.elapsed_sec}s`,
      });
      refreshStatus();
    } catch (err) {
      toast.error('Hydration failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setStarting(false);
      setRunning(false);
    }
  };

  const stopHydration = async () => {
    try {
      await axios.post(
        `${API}/admin/hydrate-non-dividend-universe/stop`,
        null,
        { withCredentials: true }
      );
      toast.info('Stop signal sent — current ticker will finish, then halt');
    } catch (err) {
      toast.error('Stop failed', { description: err?.message });
    }
  };

  const lastResult = status?.last_result;
  const progress = status?.in_progress_total
    ? Math.round((status.in_progress_processed / status.in_progress_total) * 100)
    : 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-hydrate-non-div-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-900 dark:text-indigo-200 border border-indigo-500/40 transition-colors"
        title="One-time bulk hydration of non-dividend tickers from us_market_universe into stock_cache"
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
        Hydrate non-div universe
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setOpen(false)}
          data-testid="admin-hydrate-non-div-modal"
        >
          <div
            className="relative w-full max-w-xl bg-card border border-indigo-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Hydrate Non-Dividend Universe</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Bulk-loads non-div tickers from us_market_universe into stock_cache.
                </p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="admin-hydrate-non-div-close" className="p-1 rounded hover:bg-muted/40">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {running ? (
                <div className="space-y-2" data-testid="admin-hydrate-non-div-progress">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono">{status?.in_progress_processed ?? 0} / {status?.in_progress_total ?? '?'}</span>
                    <span className="font-bold text-indigo-300">{progress}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <button
                    onClick={stopHydration}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 border border-rose-500/40"
                    data-testid="admin-hydrate-non-div-stop"
                  >
                    <Square className="w-4 h-4" />
                    Stop after current ticker
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Reads from <code className="text-foreground bg-muted/30 px-1 rounded">us_market_universe</code> where{' '}
                    <code className="text-foreground bg-muted/30 px-1 rounded">is_non_dividend=true</code>, skips
                    tickers already in <code className="text-foreground bg-muted/30 px-1 rounded">stock_cache</code>,
                    fetches prices via Full Market Snapshot (1 API call) and company details via Ticker Overview
                    (20-concurrent). Runtime ~10-15 min for ~4,500 targets.
                  </p>
                  <button
                    onClick={startHydration}
                    disabled={starting}
                    data-testid="admin-hydrate-non-div-start"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border border-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                    Start hydration
                  </button>
                </>
              )}

              {lastResult && (
                <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-1" data-testid="admin-hydrate-non-div-last-result">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Last run</p>
                  <div className="text-xs font-mono space-y-0.5">
                    <div>Targets: <span className="text-foreground font-bold">{lastResult.targets}</span></div>
                    <div>Inserted: <span className="text-emerald-300 font-bold">{lastResult.inserted}</span></div>
                    <div>Errors: <span className={lastResult.errors > 0 ? 'text-amber-300 font-bold' : 'text-muted-foreground'}>{lastResult.errors}</span></div>
                    <div>Elapsed: <span className="text-foreground">{lastResult.elapsed_sec}s</span></div>
                    {status?.last_completed_at && (
                      <div className="text-[10px] text-muted-foreground mt-1">Completed: {new Date(status.last_completed_at).toLocaleString()}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
